import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type StoreService from 'ember-data/store';
import type { PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type VisitorProfile from 'garaje/models/visitor-profile';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';

import type VisitorProfilesIndexController from './controller';

export interface VisitorsProfilesRouteModel {
  visitorProfiles: VisitorProfile[];
  visitorProfileCount: number;
}

interface QueryParams {
  page: number;
  displayName: string;
}

export const PAGE_SIZE = 50;

export default class VisitorProfilesIndexRoute extends Route {
  @service declare router: RouterService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare store: StoreService;
  @service declare state: StateService;

  queryParams = {
    page: { refreshModel: true },
    displayName: { refreshModel: true },
  };

  async model({ page, displayName }: QueryParams): Promise<VisitorsProfilesRouteModel> {
    const offset = PAGE_SIZE * (page - 1);
    const filter: Record<string, string> = {};
    if (displayName.trim()) {
      filter['display-name'] = displayName.trim();
    }

    const visitorProfiles = <PaginatedRecordArray<VisitorProfile>>await this.store.query('visitor-profile', {
      page: {
        offset,
        limit: PAGE_SIZE,
      },
      filter,
      include: 'relevant-visit',
      adapterOptions: {
        locationId: this.state.currentLocation.id,
      },
    });

    return {
      visitorProfiles: visitorProfiles.slice(),
      visitorProfileCount: visitorProfiles.meta.total,
    };
  }

  setupController(
    controller: VisitorProfilesIndexController,
    model: VisitorsProfilesRouteModel,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    // Reset loading states after model updates
    controller.directoryIsLoading = false;
  }
}
